var _a, _b, _c;
import { __decorate, __metadata } from "tslib";
import { Component } from 'decorators';
import template from './SchemaNodeEditor.view.html';
import { SchemaStoreErrors, SchemaTypeFactory } from '../../../../store/JsonSchemaStore';
import './SchemaNodeEditor.scss';
import { ALL_TYPES, ARRAY_TYPES } from '../../../../types/instructions';
import { SchemaUtils } from '../../../../utils/SchemaUtils';
const RENDERING_DELAY = 100;
let SchemaNodeEditorComponent = class SchemaNodeEditorComponent {
    static $inject = ['$translate', '$timeout'];
    //#endregion
    constructor($translate, $timeout) {
        this.$translate = $translate;
        this.$timeout = $timeout;
        this.hideRequiredToggle = false;
        //#endregion
        //#region Properties
        this.isExpanded = true;
        this.iconTypeMapping = {
            string: 'ab',
            number: '123',
            boolean: 'appearance',
            array: 'array',
            object: 'object',
        };
        this.propEntries = [];
        this.itemEntries = [];
        this.itemsPath = '';
        this.firstLoading = true;
        this.lastSignature = '';
        this.isRequired = false;
        this.errorMsg = '';
        this.hasEmptyKeyChildren = false;
        this.types = ALL_TYPES;
        this.primitiveTypes = ARRAY_TYPES;
        this.onKeyChange = (event) => {
            const newKey = event?.detail?.value ?? '';
            const isEmptyKey = !newKey || newKey.trim() === '';
            const result = this.store.renameProperty(this.objectPath, this.key, newKey);
            if (!('error' in result)) {
                this.errorMsg = isEmptyKey ?
                    this.$translate.instant('SchemaNodeEditor.errorMsg.emptyField') : '';
                return;
            }
            if (result.error === SchemaStoreErrors.DuplicateProperty) {
                this.errorMsg = this.$translate.instant('SchemaNodeEditor.errorMsg.duplicateProperty');
                return;
            }
            this.errorMsg = this.$translate.instant('SchemaNodeEditor.errorMsg.renameProperty');
        };
        this.onDescriptionChange = (event) => {
            const newDescription = event.detail.value;
            this.store.setDescription(this.path, newDescription);
        };
        this.onRequiredChange = (event) => {
            const isRequired = event.detail.checked;
            this.store.setRequired(this.objectPath, this.key, isRequired);
        };
        //#region Items Handling
        this.onChangeItem = (event) => {
            const { value } = event.detail;
            this.store.setItems(this.path, value);
        };
    }
    //#region Lifecycle Hooks
    $onInit() {
        this.lastSignature = this.buildSignature();
        this.computePath();
    }
    $onChanges(changes) {
        if (!changes.path?.isFirstChange() || !changes?.node?.isFirstChange()) {
            this.lastSignature = this.buildSignature();
            this.computePath();
        }
    }
    $doCheck() {
        const signature = this.buildSignature();
        this.isRequired = this.store.isRequired(this.objectPath, this.key);
        if (signature !== this.lastSignature) {
            this.lastSignature = signature;
            this.computePath();
        }
    }
    //#endregion
    //#region Methods
    computePath() {
        this.propEntries = [];
        this.itemsPath = '';
        this.itemEntries = [];
        if (this.node?.type === 'object') {
            const props = this.node.properties ?? {};
            this.propEntries = this.structureChildrenNodes(props, this.path);
        }
        if (this.node?.type === 'array' && this.node?.items?.type === 'object') {
            this.itemsPath = `${this.path}/items`;
            const properties = this.node.items.properties ?? {};
            this.itemEntries = this.structureChildrenNodes(properties, this.itemsPath);
        }
        this.hasEmptyKeyChildren = this.propEntries.some(this.hasEmptyKeyChildrenFilter) ||
            this.itemEntries.some(this.hasEmptyKeyChildrenFilter);
        if (this.firstLoading) {
            this.$timeout(() => {
                this.firstLoading = false;
            }, RENDERING_DELAY);
        }
    }
    toggleExpand() {
        this.isExpanded = !this.isExpanded;
    }
    onTypeChange(newType) {
        if (newType === this.node.type) {
            return;
        }
        this.store.setNodeType(this.path, newType);
        this.dropdownRef[0]?.setClose?.();
    }
    hasNestedItems() {
        return this.node.type === 'array' &&
            this.node.items?.type === 'object' &&
            !!this.node.items.properties && Object.keys(this.node.items.properties).length > 0;
    }
    handleAddItemProperty() {
        if (this.node?.type === 'array' && this.node?.items?.type === 'object') {
            this.store.upsertProperty(this.itemsPath, '', SchemaTypeFactory.string(''));
        }
    }
    //#endregion
    handleDelete() {
        this.store.remove(this.path);
    }
    handleAddProperty() {
        this.store.upsertProperty(this.path, '', SchemaTypeFactory.string(''));
    }
    hasNestedProperties() {
        return this.node.type === 'object' && !!this.node.properties && Object.keys(this.node.properties).length > 0;
    }
    //#region Helper Methods
    structureChildrenNodes(props, nodesPath) {
        return Object
            .entries(props)
            .sort((a, b) => SchemaUtils.sortNodesByPosition(a[1], b[1]))
            .map(([key, node]) => ({
            key,
            node,
            id: node.__uid + '_' + node.type,
            path: `${nodesPath}/properties/${key}`,
            objectPath: nodesPath,
        }));
    }
    buildSignature() {
        if (!this.node) {
            return '';
        }
        if (this.node?.type === 'object') {
            const props = this.node.properties ?? {};
            return this.buildSortedSignature(props);
        }
        if (this.node?.type === 'array') {
            if (this.node?.items?.type === 'object') {
                const itemsProps = this.node.items.properties ?? {};
                return this.buildSortedSignature(itemsProps);
            }
            return `items:${this.node.items.__uid}`;
        }
        return this?.node?.type ?? '';
    }
    buildSortedSignature(itemsProps) {
        return Object.keys(itemsProps)
            .sort((a, b) => SchemaUtils.sortNodesByPosition(itemsProps[a], itemsProps[b]))
            .map((key) => `${key}:${itemsProps[key].__uid}`)
            .join('|');
    }
    hasEmptyKeyChildrenFilter(entry) {
        return !entry.key || entry.key.trim() === '';
    }
};
SchemaNodeEditorComponent = __decorate([
    Component({
        template,
        bindings: {
            path: '<',
            node: '<',
            store: '<',
            key: '<',
            objectPath: '<',
            hideRequiredToggle: '<',
        },
    }),
    __metadata("design:paramtypes", [typeof (_b = typeof angular !== "undefined" && (_a = angular.translate) !== void 0 && _a.ITranslateService) === "function" ? _b : Object, typeof (_c = typeof angular !== "undefined" && angular.ITimeoutService) === "function" ? _c : Object])
], SchemaNodeEditorComponent);
export { SchemaNodeEditorComponent };
